/**
 * Parent-page bridge for commerce reassurance sections below the HOLO iframe.
 */
(function () {
  const CREATOR_SELECTOR = "[data-holo-creator]";
  const COMMERCE_SECTION_SELECTOR = ".holo-commerce-reassure-section";
  const PHASE_SECTION_SELECTOR = ".holo-creator-phase-section";

  function resolveHoloOrigin() {
    const root = document.querySelector(CREATOR_SELECTOR);
    const fromData = root?.getAttribute("data-holo-origin");
    if (fromData) {
      try {
        return new URL(fromData, window.location.origin).origin;
      } catch {
        /* ignore */
      }
    }
    const iframe = getIframe();
    if (iframe?.src) {
      try {
        return new URL(iframe.src, window.location.origin).origin;
      } catch {
        /* ignore */
      }
    }
    return "";
  }

  function getIframe() {
    return document.querySelector(`${CREATOR_SELECTOR} iframe, .spark-stack-holo-frame`);
  }

  function isTrustedHoloMessage(event) {
    const origin = resolveHoloOrigin();
    if (origin && event.origin !== origin) return false;
    const data = event.data;
    return Boolean(data && data.source === "sparkstack-holo");
  }

  function isDesignMode() {
    return Boolean(window.Shopify?.designMode);
  }

  function setReassureActive(active) {
    if (isDesignMode()) {
      document.body.classList.add("holo-commerce-reassure-active");
      document.querySelectorAll(COMMERCE_SECTION_SELECTOR).forEach((section) => {
        section.hidden = false;
      });
      syncCreatorPhaseVisibility();
      return;
    }

    document.body.classList.toggle("holo-commerce-reassure-active", Boolean(active));
    document.querySelectorAll(COMMERCE_SECTION_SELECTOR).forEach((section) => {
      section.hidden = !active;
    });
    syncCreatorPhaseVisibility();
  }

  function setCreatorPhaseActive(active) {
    if (isDesignMode()) {
      document.body.classList.add("holo-creator-phase-active");
      document.querySelectorAll(PHASE_SECTION_SELECTOR).forEach((section) => {
        section.hidden = false;
      });
      return;
    }

    document.body.classList.toggle("holo-creator-phase-active", Boolean(active));
    document.querySelectorAll(PHASE_SECTION_SELECTOR).forEach((section) => {
      section.hidden = !active;
    });
  }

  function syncCreatorPhaseVisibility() {
    if (isDesignMode()) {
      setCreatorPhaseActive(true);
      return;
    }

    const creatorActive = document.body.classList.contains("holo-creator-active");
    setCreatorPhaseActive(creatorActive);
  }

  function applyCommerceState(state) {
    if (!state || typeof state !== "object") return;

    const setText = (selector, value) => {
      if (value == null || value === "") return;
      document.querySelectorAll(selector).forEach((el) => {
        el.textContent = String(value);
      });
    };

    setText("[data-holo-commerce-layers]", state.layers);
    setText("[data-holo-commerce-size-name]", state.sizeName);
    setText("[data-holo-commerce-dimensions]", state.dimensions);
    setText("[data-holo-commerce-material]", state.material);
    setText("[data-holo-commerce-ships-label]", state.shipsLabel);
    setText("[data-holo-commerce-faq-size]", state.faqSizeAnswer);

    document.querySelectorAll("[data-holo-commerce-size-rectangle]").forEach((el) => {
      el.hidden = state.shape === "square";
    });
    document.querySelectorAll("[data-holo-commerce-size-square]").forEach((el) => {
      el.hidden = state.shape !== "square";
    });
  }

  function postToIframe(message) {
    const iframe = getIframe();
    if (!iframe?.contentWindow) return;
    const origin = resolveHoloOrigin();
    if (!origin) return;
    try {
      iframe.contentWindow.postMessage(message, origin);
    } catch {
      /* ignore */
    }
  }

  function activateLazyVideo(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    const src = video.getAttribute("data-src") || video.getAttribute("src") || "";
    if (!src) return;
    if (video.getAttribute("src") !== src) {
      video.setAttribute("src", src);
      video.load?.();
    }
  }

  function initLazyVideos() {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const nodes = document.querySelectorAll("[data-commerce-lazy-video]");
    if (!nodes.length || reduced) return;

    if (!("IntersectionObserver" in window)) {
      nodes.forEach((video) => {
        activateLazyVideo(video);
        video.play?.().catch(() => {});
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) return;
          if (entry.isIntersecting && entry.intersectionRatio > 0.15) {
            activateLazyVideo(video);
            video.play?.().catch(() => {});
          } else {
            try {
              video.pause();
            } catch {
              /* ignore */
            }
          }
        });
      },
      { root: null, rootMargin: "120px 0px", threshold: [0, 0.15, 0.4] },
    );

    nodes.forEach((video) => observer.observe(video));
  }

  document.addEventListener("click", (event) => {
    const btn = event.target.closest?.("[data-holo-commerce-see-size]");
    if (!btn) return;
    event.preventDefault();
    postToIframe({ type: "HOLO_SCROLL_TO_SIZE", source: "sparkstack-holo-theme" });
  });

  window.addEventListener("message", (event) => {
    if (!isTrustedHoloMessage(event)) return;
    const data = event.data;

    if (data.type === "HOLO_PREVIEW_COMMERCE") {
      setReassureActive(Boolean(data.active));
      if (data.active) {
        window.requestAnimationFrame(initLazyVideos);
      }
      return;
    }

    if (data.type === "HOLO_COMMERCE_STATE") {
      applyCommerceState(data.state || data);
    }
  });

  window.HoloCommerceBridge = {
    setReassureActive,
    setCreatorPhaseActive,
    syncCreatorPhaseVisibility,
    applyCommerceState,
    postToIframe,
  };

  if (isDesignMode()) {
    setReassureActive(true);
  } else {
    syncCreatorPhaseVisibility();
  }

  initLazyVideos();
})();
